import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Memory Grid — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/memory-grid.md §FTUE
 *   E1 : séquence 3 (grille 4×4, forward) — pédagogique.
 *   E2 : séquence 4 — "on augmente d'un cran".
 *   E3 : séquence 5 — calibration baseline.
 *
 * Pas de scoring engine — pédagogique uniquement, démo passe ou skip.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { timing } from './params';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'memory_grid.demo.step1_title',
        hintKey: 'memory_grid.demo.step1_hint',
        gridSize: 4,
        cells: [
            { row: 0, col: 1 },
            { row: 2, col: 2 },
            { row: 1, col: 0 },
        ],
    },
    {
        idx: 2,
        titleKey: 'memory_grid.demo.step2_title',
        hintKey: 'memory_grid.demo.step2_hint',
        gridSize: 4,
        cells: [
            { row: 0, col: 0 },
            { row: 1, col: 3 },
            { row: 3, col: 1 },
            { row: 2, col: 2 },
        ],
    },
    {
        idx: 3,
        titleKey: 'memory_grid.demo.step3_title',
        hintKey: 'memory_grid.demo.step3_hint',
        gridSize: 4,
        cells: [
            { row: 0, col: 2 },
            { row: 2, col: 0 },
            { row: 1, col: 1 },
            { row: 3, col: 3 },
            { row: 0, col: 0 },
        ],
    },
];
function cellEq(a, b) {
    return a.row === b.row && a.col === b.col;
}
function cellKey(c) {
    return `${c.row}-${c.col}`;
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [sub, setSub] = useState('idle');
    const [presentingIdx, setPresentingIdx] = useState(-1);
    const [taps, setTaps] = useState([]);
    const [verdict, setVerdict] = useState(null);
    const timerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };
    const playSequence = () => {
        clearTimer();
        setTaps([]);
        setVerdict(null);
        setSub('presenting');
        setPresentingIdx(0);
    };
    // Présenter cellule par cellule
    useEffect(() => {
        if (sub !== 'presenting')
            return;
        clearTimer();
        if (presentingIdx < 0)
            return;
        if (presentingIdx >= step.cells.length) {
            setPresentingIdx(-1);
            setSub('awaiting');
            return;
        }
        timerRef.current = setTimeout(() => {
            setPresentingIdx((i) => i + 1);
        }, timing.cellLitMs + timing.isiMs);
        return () => clearTimer();
    }, [sub, presentingIdx, step.cells.length]);
    useEffect(() => () => clearTimer(), []);
    const handleTap = (cell) => {
        if (sub !== 'awaiting')
            return;
        const newTaps = [...taps, cell];
        setTaps(newTaps);
        const expected = step.cells.slice(0, newTaps.length);
        const correctSoFar = newTaps.every((tt, i) => cellEq(tt, expected[i]));
        if (!correctSoFar) {
            setVerdict('incorrect');
            setSub('feedback');
            return;
        }
        if (newTaps.length === step.cells.length) {
            setVerdict('correct');
            setSub('feedback');
        }
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setSub('idle');
        setPresentingIdx(-1);
        setTaps([]);
        setVerdict(null);
    };
    const cells = [];
    for (let r = 0; r < step.gridSize; r += 1) {
        for (let c = 0; c < step.gridSize; c += 1)
            cells.push({ row: r, col: c });
    }
    const litKey = sub === 'presenting' && presentingIdx >= 0 && presentingIdx < step.cells.length
        ? cellKey(step.cells[presentingIdx])
        : null;
    const expectedSet = new Set(step.cells.map(cellKey));
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('memory_grid.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("div", { className: "grid w-full max-w-[320px] gap-s2 border border-cyan-border-faint bg-cyan-surface p-s4", style: { gridTemplateColumns: `repeat(${step.gridSize}, minmax(0, 1fr))`, aspectRatio: '1 / 1' }, children: cells.map((cell) => {
                    const k = cellKey(cell);
                    const isLit = litKey === k;
                    const tapIdx = taps.findIndex((tt) => cellEq(tt, cell));
                    const isTapped = tapIdx >= 0;
                    const isExpected = expectedSet.has(k);
                    const showVerdict = sub === 'feedback';
                    let cls = 'bg-cyan-bg text-cyan-text-secondary';
                    if (isLit)
                        cls = 'bg-cyan-network text-cyan-surface scale-95';
                    else if (isTapped && verdict === 'incorrect')
                        cls = 'bg-cyan-media text-cyan-surface';
                    else if (isTapped && verdict === 'correct')
                        cls = 'bg-cyan-core text-cyan-surface';
                    else if (isTapped)
                        cls = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
                    else if (showVerdict && verdict === 'incorrect' && isExpected)
                        cls = 'bg-cyan-core/10 text-cyan-text border border-cyan-core/30';
                    return (_jsx("button", { type: "button", disabled: sub !== 'awaiting', onClick: () => handleTap(cell), className: [
                            'flex aspect-square items-center justify-center transition-all duration-feedback ease-soft-wind',
                            cls,
                            sub === 'awaiting' ? 'cursor-pointer hover:bg-cyan-border-faint active:scale-95' : 'cursor-not-allowed',
                        ].join(' '), "aria-label": t('memory_grid.demo.cell_aria', { row: cell.row + 1, col: cell.col + 1 }), children: isTapped && tapIdx >= 0 && _jsx("span", { className: "font-mono text-[14px]", children: tapIdx + 1 }) }, k));
                }) }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [sub === 'idle' && (_jsx("button", { type: "button", onClick: playSequence, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('memory_grid.demo.launch_sequence') })), sub === 'presenting' && (_jsx("span", { className: "font-display text-[14px] text-cyan-network", children: t('memory_grid.demo.watch_sequence') })), sub === 'awaiting' && (_jsx("span", { className: "text-[12px] text-cyan-text-secondary", children: t('memory_grid.demo.your_turn') })), sub === 'feedback' && (_jsxs(_Fragment, { children: [_jsx("span", { className: [
                                    'font-display text-[16px] font-medium',
                                    verdict === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                                ].join(' '), children: verdict === 'correct'
                                    ? t('memory_grid.demo.feedback_correct')
                                    : t('memory_grid.demo.feedback_incorrect') }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') }), _jsx("button", { type: "button", onClick: playSequence, className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: t('memory_grid.demo.replay_sequence') })] })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map